import { Close, Search } from "@mui/icons-material";
import styles from "./search-modal.module.css";
import { MdApartment, MdBusiness, MdLandscape } from "react-icons/md";
import { FaHandHolding } from "react-icons/fa6";
import { LuHouse, LuHousePlus, LuKeyRound } from "react-icons/lu";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { PurposeEnum, TypeEnum } from "@/types/types";
import { useListingsCacheStore } from "@/global-cache/useListingsCacheStore";
import { BsHousesFill } from "react-icons/bs";
import { useSavedListingsStore } from "../useSavedListingsStore";
import { createPortal } from "react-dom";

export default function SearchModal({
  onClose,
  sessionId,
}: {
  onClose: () => void;
  sessionId: string | null;
}) {
  const [mounted, setMounted] = useState(false);
  const handleCloseClick = (e: any) => {
    e.preventDefault();
    onClose();
  };
  const type = useSavedListingsStore((state) => state.type);
  const purpose = useSavedListingsStore((state) => state.purpose);
  const text = useSavedListingsStore((state) => state.text);
  const setType = useSavedListingsStore((state) => state.setType);
  const setPurpose = useSavedListingsStore((state) => state.setPurpose);
  const onTextChange = useSavedListingsStore((state) => state.onTextChange);
  const setCache = useListingsCacheStore((state) => state.setMultipleListings);
  const t = useTranslations("SearchModal");
  const locale = useLocale();
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [initialPurpose, setInitialPurpose] = useState(PurposeEnum.DEFAULT);
  const [initialType, setInitialType] = useState(TypeEnum.DEFAULT);
  const refetchState = useSavedListingsStore().refetchState;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setInitialPurpose(purpose);
    setInitialType(type);
  }, []);

  const scrollRef = useRef<HTMLUListElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      setIsScrolling(true);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 100); // small delay after scroll ends
    };

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let isDragging = false;
    let startX: number;
    let scrollLeft: number;
    let dragThreshold = 5; // pixels

    let downX = 0;

    const onMouseDown = (e: MouseEvent) => {
      isDragging = false;
      startX = e.pageX - container.offsetLeft;
      downX = e.pageX;
      scrollLeft = container.scrollLeft;

      container.addEventListener("mousemove", onMouseMove);
      container.addEventListener("mouseup", onMouseUp);
    };

    const onMouseMove = (e: MouseEvent) => {
      const x = e.pageX - container.offsetLeft;
      const walk = x - startX;
      if (Math.abs(e.pageX - downX) > dragThreshold) {
        isDragging = true;
      }
      container.scrollLeft = scrollLeft - walk;
    };

    const onMouseUp = (e: MouseEvent) => {
      container.removeEventListener("mousemove", onMouseMove);
      container.removeEventListener("mouseup", onMouseUp);
    };

    const onClick = (e: MouseEvent) => {
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    container.addEventListener("mousedown", onMouseDown);
    container.addEventListener("click", onClick, true); // use capture to catch early

    return () => {
      container.removeEventListener("mousedown", onMouseDown);
      container.removeEventListener("click", onClick, true);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      const target = event.target as HTMLElement;
      if (target !== inputRef.current) {
        inputRef.current?.blur();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);
  const onCloseRef = useRef(onClose);

  // Always keep the ref perfectly synced with the latest render
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const searchModalId = useRef(`search-${Date.now()}`).current;

  const currentFiltersRef = useRef({
    type,
    purpose,
    initialType,
    initialPurpose,
  });
  useEffect(() => {
    currentFiltersRef.current = { type, purpose, initialType, initialPurpose };
  }, [type, purpose, initialType, initialPurpose]);

  const handleClose = useCallback(() => {
    if (initialType !== type) setType(initialType);
    if (initialPurpose !== purpose) setPurpose(initialPurpose);

    if (window.history.state?.searchModalId === searchModalId) {
      window.history.back();
    }
    onCloseRef.current();
  }, [
    initialType,
    type,
    setType,
    initialPurpose,
    purpose,
    setPurpose,
    searchModalId,
  ]);

  const hasPushedState = useRef(false);

  useEffect(() => {
    if (!mounted) return;

    if (!hasPushedState.current) {
      window.history.pushState({ ...window.history.state, searchModalId }, "");
      hasPushedState.current = true;
    }

    const handlePopState = (e: PopStateEvent) => {
      if (e.state && e.state.searchModalId === searchModalId) return;

      const {
        type: curType,
        purpose: curPurpose,
        initialType: initT,
        initialPurpose: initP,
      } = currentFiltersRef.current;
      if (initT !== curType) setType(initT);
      if (initP !== curPurpose) setPurpose(initP);

      onCloseRef.current();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("popstate", handlePopState);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mounted, searchModalId, setType, setPurpose, handleClose]);

  if (!mounted) return null;

  return createPortal(
    <div className={styles.modalWrapper} ref={wrapperRef}>
      <div className={styles.topBar}>
        <div className={styles.aligner}></div>
        <h3>{t("search")}</h3>
        <button
          onClick={(e) => {
            e.preventDefault();
            handleClose();
          }}
        >
          <Close />
        </button>
      </div>

      <div className={styles.scrollableContent}>
        {/* Where Section */}
        <div className={styles.contentSection}>
          <p className={styles.sectionTitle}>{t("where")}</p>
          <div className={styles.inputAndSuggestionsWrapper}>
            <div className={styles.inputAndNumberWrapper}>
              <input
                ref={inputRef}
                className={styles.input}
                type="text"
                placeholder={t("enterLocation")}
                value={text}
                onChange={(e) => onTextChange(e, locale, sessionId, setCache)}
              />
            </div>
          </div>
        </div>

        {/* Type Section */}
        <div className={styles.contentSection}>
          <p className={styles.sectionTitle}>{t("type")}</p>
          <ul ref={scrollRef} className={styles.pillButtonsWrapper}>
            <li>
              <button
                className={`${styles.pillButton} ${type === TypeEnum.APARTMENT_HOUSE && styles.isActive}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isScrolling) setType(TypeEnum.APARTMENT_HOUSE);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isScrolling) setType(TypeEnum.APARTMENT_HOUSE);
                }}
              >
                <BsHousesFill className={styles.pillIcon} />
                <span className={styles.text}>{t("apartment-house")}</span>
              </button>
            </li>
            <li>
              <button
                className={`${styles.pillButton} ${type === TypeEnum.APARTMENT && styles.isActive}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isScrolling) setType(TypeEnum.APARTMENT);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isScrolling) setType(TypeEnum.APARTMENT);
                }}
              >
                <MdApartment className={styles.pillIcon} />
                <span className={styles.text}>{t("apartment")}</span>
              </button>
            </li>
            <li>
              <button
                className={`${styles.pillButton} ${type === TypeEnum.HOUSE && styles.isActive}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isScrolling) setType(TypeEnum.HOUSE);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isScrolling) setType(TypeEnum.HOUSE);
                }}
              >
                <LuHouse className={styles.pillIcon} />
                <span className={styles.text}>{t("house")}</span>
              </button>
            </li>
            <li>
              <button
                className={`${styles.pillButton} ${type === TypeEnum.LAND && styles.isActive}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isScrolling) setType(TypeEnum.LAND);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isScrolling) setType(TypeEnum.LAND);
                }}
              >
                <MdLandscape className={styles.pillIcon} />
                <span className={styles.text}>{t("land")}</span>
              </button>
            </li>
            <li>
              <button
                className={`${styles.pillButton} ${type === TypeEnum.OFFICE && styles.isActive}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isScrolling) setType(TypeEnum.OFFICE);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isScrolling) setType(TypeEnum.OFFICE);
                }}
              >
                <MdBusiness className={styles.pillIcon} />
                <span className={styles.text}>{t("office")}</span>
              </button>
            </li>
            <li>
              <button
                className={`${styles.pillButton} ${type === TypeEnum.DEFAULT && styles.isActive}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isScrolling) setType(TypeEnum.DEFAULT);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (!isScrolling) setType(TypeEnum.DEFAULT);
                }}
              >
                <span className={styles.text}>{t("any")}</span>
              </button>
            </li>
          </ul>
        </div>

        {/* Purpose Section */}
        <div className={styles.contentSection}>
          <p className={styles.sectionTitle}>{t("purpose")}</p>
          <ul className={styles.pillButtonsWrapper}>
            <li>
              <button
                className={`${styles.pillButton} ${purpose === PurposeEnum.FORSALE && styles.isActive}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setPurpose(PurposeEnum.FORSALE);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setPurpose(PurposeEnum.FORSALE);
                }}
              >
                <LuHousePlus className={styles.pillIcon} />
                <span className={styles.text}>{t("for-sale")}</span>
              </button>
            </li>
            <li>
              <button
                className={`${styles.pillButton} ${purpose === PurposeEnum.FORRENT && styles.isActive}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setPurpose(PurposeEnum.FORRENT);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setPurpose(PurposeEnum.FORRENT);
                }}
              >
                <LuKeyRound className={styles.pillIcon} />
                <span className={styles.text}>{t("for-rent")}</span>
              </button>
            </li>
            <li>
              <button
                className={`${styles.pillButton} ${purpose === PurposeEnum.DEFAULT && styles.isActive}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setPurpose(PurposeEnum.DEFAULT);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setPurpose(PurposeEnum.DEFAULT);
                }}
              >
                <span className={styles.text}>{t("any")}</span>
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Floating CTA */}
      <div className={styles.floatingButtonWrapper}>
        <button
          className={styles.searchButton}
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (window.history.state?.searchModalId === searchModalId) {
              window.history.back();
            }
            onClose();
            await refetchState(locale, sessionId, setCache);
          }}
        >
          <p>{t("search")}</p>
          <Search />
        </button>
      </div>
    </div>,
    document.body,
  );
}
